import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  listFiles,
  deleteFile,
  getStorageUsage,
  saveFile,
  clearAllStorage,
  formatBytes,
  type StorageUsage,
} from '@/utils/offlineStorage';
import { api } from '@/services/api';

interface DownloadedFile {
  fileId: string;
  songId: string;
  title: string;
  artist?: string;
  voicePartName?: string;
  voicePartId?: string;
  downloadedAt: number;
}

interface DownloadProgress {
  fileId: string;
  progress: number; // 0-100
  status: 'downloading' | 'complete' | 'error';
  error?: string;
}

interface OfflineContextType {
  // Network state
  isOnline: boolean;

  // Storage
  downloadedFiles: DownloadedFile[];
  offlineSongIds: Set<string>; // Songs that have at least one offline file
  storageUsage: StorageUsage;
  storageUsageFormatted: string;

  // Download operations
  downloadProgress: Map<string, DownloadProgress>;
  downloadFile: (fileId: string, songId: string, metadata: {
    title: string;
    artist?: string;
    voicePartName?: string;
    voicePartId?: string;
  }) => Promise<void>;
  removeFile: (fileId: string) => Promise<void>;
  clearAll: () => Promise<void>;

  // Helpers
  isFileDownloaded: (fileId: string) => boolean;
  isSongOffline: (songId: string) => boolean;
  refreshDownloadedFiles: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextType | null>(null);

export function OfflineProvider({ children }: { children: React.ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [downloadedFiles, setDownloadedFiles] = useState<DownloadedFile[]>([]);
  const [storageUsage, setStorageUsage] = useState<StorageUsage>({ used: 0, total: 0, percentage: 0 });
  const [downloadProgress, setDownloadProgress] = useState<Map<string, DownloadProgress>>(new Map());
  const [downloadedFileIds, setDownloadedFileIds] = useState<Set<string>>(new Set());
  const [offlineSongIds, setOfflineSongIds] = useState<Set<string>>(new Set());

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load downloaded files on mount
  const refreshDownloadedFiles = useCallback(async () => {
    try {
      const files = await listFiles();
      const downloaded: DownloadedFile[] = files.map(f => ({
        fileId: f.fileId,
        songId: f.songId,
        title: f.title,
        artist: f.artist,
        voicePartName: f.voicePartName,
        voicePartId: f.voicePartId,
        downloadedAt: f.downloadedAt,
      }));
      setDownloadedFiles(downloaded);
      setDownloadedFileIds(new Set(downloaded.map(f => f.fileId)));
      setOfflineSongIds(new Set(downloaded.map(f => f.songId)));

      const usage = await getStorageUsage();
      setStorageUsage(usage);
    } catch (err) {
      console.error('Failed to load offline files:', err);
    }
  }, []);

  useEffect(() => {
    refreshDownloadedFiles();
  }, [refreshDownloadedFiles]);

  // Download a file
  const downloadFile = useCallback(async (
    fileId: string,
    songId: string,
    metadata: {
      title: string;
      artist?: string;
      voicePartName?: string;
      voicePartId?: string;
    }
  ) => {
    // Check if already downloaded
    if (downloadedFileIds.has(fileId)) return;

    // Set progress
    setDownloadProgress(prev => {
      const next = new Map(prev);
      next.set(fileId, { fileId, progress: 0, status: 'downloading' });
      return next;
    });

    try {
      // Fetch the file
      const url = api.getAuthenticatedFileUrl(fileId);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Get the blob
      const blob = await response.blob();

      // Save to IndexedDB
      await saveFile(fileId, songId, metadata.voicePartId, blob, {
        songId,
        title: metadata.title,
        artist: metadata.artist,
        voicePartName: metadata.voicePartName,
        voicePartId: metadata.voicePartId,
      });

      // Update progress
      setDownloadProgress(prev => {
        const next = new Map(prev);
        next.set(fileId, { fileId, progress: 100, status: 'complete' });
        return next;
      });

      // Refresh files list
      await refreshDownloadedFiles();

      // Clear progress after a delay
      setTimeout(() => {
        setDownloadProgress(prev => {
          const next = new Map(prev);
          next.delete(fileId);
          return next;
        });
      }, 2000);

    } catch (err) {
      console.error('Download failed:', err);
      setDownloadProgress(prev => {
        const next = new Map(prev);
        next.set(fileId, {
          fileId,
          progress: 0,
          status: 'error',
          error: err instanceof Error ? err.message : 'Download mislukt',
        });
        return next;
      });
    }
  }, [downloadedFileIds, refreshDownloadedFiles]);

  // Remove a file
  const removeFile = useCallback(async (fileId: string) => {
    try {
      await deleteFile(fileId);
      await refreshDownloadedFiles();
    } catch (err) {
      console.error('Failed to remove file:', err);
    }
  }, [refreshDownloadedFiles]);

  // Clear all storage
  const clearAll = useCallback(async () => {
    try {
      await clearAllStorage();
      await refreshDownloadedFiles();
    } catch (err) {
      console.error('Failed to clear storage:', err);
    }
  }, [refreshDownloadedFiles]);

  // Check if a file is downloaded
  const isFileDownloaded = useCallback((fileId: string) => {
    return downloadedFileIds.has(fileId);
  }, [downloadedFileIds]);

  // Check if a song has any offline files
  const isSongOffline = useCallback((songId: string) => {
    return offlineSongIds.has(songId);
  }, [offlineSongIds]);

  const value: OfflineContextType = {
    isOnline,
    downloadedFiles,
    offlineSongIds,
    storageUsage,
    storageUsageFormatted: formatBytes(storageUsage.used),
    downloadProgress,
    downloadFile,
    removeFile,
    clearAll,
    isFileDownloaded,
    isSongOffline,
    refreshDownloadedFiles,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline moet binnen een OfflineProvider worden gebruikt');
  }
  return context;
}
